import React from "react";
import { IAudioVideoTranscriptItem, IHighlightedDialog } from "../models/interfaces";

const AUDIO_VIDEO_EXTENSIONS = ["mp4", "webm", "amr", "flac", "m4a", "mp3", "ogg", "oga", "opus", "wav"];

export const isAudioVideoMedia = (ref: { file_type?: string; file_name?: string }): boolean => {
	const fileType = ref?.file_type?.toLowerCase() || "";
	const fileName = ref?.file_name?.toLowerCase() || "";
	const extension = fileName.split(".").pop() || "";
	return AUDIO_VIDEO_EXTENSIONS.includes(fileType) || AUDIO_VIDEO_EXTENSIONS.includes(extension);
};

// Keep isVideoMedia as a separate helper for distinguishing video from audio:
export const isVideoMedia = (ref: { file_type?: string; file_name?: string }): boolean => {
	const fileType = ref?.file_type?.toLowerCase() || "";
	const fileName = ref?.file_name?.toLowerCase() || "";
	const extension = fileName.split(".").pop() || "";
	const videoExtensions = ["mp4", "webm"];
	return videoExtensions.includes(fileType) || videoExtensions.includes(extension);
};
/** Converts seconds (number | null) to mm:ss display string */
export const formatTimestamp = (seconds: number | null): string => {
	if (seconds === null || seconds === undefined || isNaN(seconds)) return "--:--";
	const totalSecs = Math.floor(seconds);
	const hrs = Math.floor(totalSecs / 3600);
	const mins = Math.floor((totalSecs % 3600) / 60);
	const secs = totalSecs % 60;
	if (hrs > 0) {
		return `${hrs.toString().padStart(2, "0")}:${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
	}
	return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
};

/** Highlights matching search text within a string by wrapping matches in <mark> */
export const highlightSearchText = (text: string, query: string): React.ReactNode => {
	const trimmedQuery = query?.trim();
	if (!trimmedQuery || trimmedQuery.length > 200) return text;

	const escaped = trimmedQuery.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

	// Split regex — needs /g to collect all match segments via String.prototype.split.
	const splitRegex = new RegExp(`(${escaped})`, "gi");

	// Test regex — NO /g flag. Anchored with ^...$ so we test whole-part equality,
	// not substring containment. Stateless; safe to reuse across .map iterations.
	const testRegex = new RegExp(`^${escaped}$`, "i");

	const parts = text.split(splitRegex);

	if (parts.length === 1) return text;

	return parts.map((part, index) =>
		testRegex.test(part) ? (
			<mark key={index} className="av-search-highlight">
				{part}
			</mark>
		) : (
			part
		),
	);
};

/** Returns highlight ranges for a specific dialog from highlighted_dialog_nos data */
export const getDialogHighlightRanges = (dialogId: number, highlightedDialogs: IHighlightedDialog[]): Array<{ start: number; end: number }> | null => {
	if (!highlightedDialogs || highlightedDialogs.length === 0) {
		return null;
	}

	const dialogData = highlightedDialogs.find((d) => parseInt(d.dialog_no, 10) === dialogId);

	if (!dialogData || !dialogData.dialog_highlights) {
		return null;
	}

	return dialogData.dialog_highlights
		.map((h) => ({
			start: parseInt(h.start_index, 10),
			end: parseInt(h.end_index, 10),
		}))
		.filter((r) => !isNaN(r.start) && !isNaN(r.end) && r.start < r.end);
};

export const highlightTextRanges = (text: string, ranges: Array<{ start: number; end: number }>): React.ReactNode => {
	if (!ranges || ranges.length === 0 || !text) {
		return text;
	}

	const sortedRanges = [...ranges].sort((a, b) => a.start - b.start);
	const mergedRanges: Array<{ start: number; end: number }> = [];

	sortedRanges.forEach((range) => {
		const validRange = {
			start: Math.max(0, Math.min(range.start, text.length)),
			end: Math.max(0, Math.min(range.end, text.length)),
		};

		if (validRange.start >= validRange.end) return;

		if (mergedRanges.length === 0) {
			mergedRanges.push(validRange);
		} else {
			const lastRange = mergedRanges[mergedRanges.length - 1];
			if (validRange.start <= lastRange.end) {
				lastRange.end = Math.max(lastRange.end, validRange.end);
			} else {
				mergedRanges.push(validRange);
			}
		}
	});

	const parts: React.ReactNode[] = [];
	let lastIndex = 0;
	let key = 0;

	mergedRanges.forEach((range) => {
		if (range.start > lastIndex) {
			parts.push(<span key={`text-${key++}`}>{text.slice(lastIndex, range.start)}</span>);
		}

		parts.push(
			<span key={`highlight-${key++}`} className="highlight-block">
				{text.slice(range.start, range.end)}
			</span>,
		);

		lastIndex = range.end;
	});

	if (lastIndex < text.length) {
		parts.push(<span key={`text-${key++}`}>{text.slice(lastIndex)}</span>);
	}

	return <>{parts}</>;
};

// --- Shared HLS Utilities ---

/**
 * Extracts signing query params from a URL and returns them as a raw query string.
 * Handles CloudFront signed URLs, S3 v4 presigned URLs, and S3 v2 presigned URLs.
 * IMPORTANT: Preserves original encoding — does NOT re-encode.
 */
export const extractSignedParams = (url: string): string => {
	try {
		const qIndex = url.indexOf("?");
		if (qIndex === -1) return "";
		const queryString = url.substring(qIndex + 1);

		if (queryString.length > 2048) return "";

		// CloudFront signed URLs (custom policy)
		if (queryString.includes("Policy=") && queryString.includes("Signature=") && queryString.includes("Key-Pair-Id=")) {
			return queryString;
		}

		// S3 / CloudFront presigned (v4 signature)
		if (queryString.includes("X-Amz-Signature=") && queryString.includes("X-Amz-Credential=")) {
			return queryString;
		}

		// S3 v2 style
		if (queryString.includes("AWSAccessKeyId=") && queryString.includes("Signature=")) {
			return queryString;
		}
	} catch {
		// ignore
	}
	return "";
};

/**
 * Extracts just the filename from a URL, stripping path and query params.
 */
export const extractFilenameFromUrl = (url: string): string => {
	try {
		const pathname = new URL(url).pathname;
		return pathname.split("/").pop() || "";
	} catch {
		const withoutQuery = url.split("?")[0];
		return withoutQuery.split("/").pop() || "";
	}
};

/**
 * Checks if a URL is an HLS manifest (.m3u8)
 */
export const isHlsManifestUrl = (url: string): boolean => {
	if (!url) return false;
	try {
		return new URL(url).pathname.endsWith(".m3u8");
	} catch {
		return url.includes(".m3u8");
	}
};

/**
 * Returns dialog_ids whose [timestamp_start, timestamp_end] interval contains `t`.
 * Uses binary search; transcript_items are assumed sorted by timestamp_start.
 * Avoids per-tick O(N) filter on long transcripts (review A6).
 */
export const findActiveDialogIds = (items: IAudioVideoTranscriptItem[] | undefined | null, t: number): number[] => {
	if (!items || items.length === 0) return [];
	let lo = 0;
	let hi = items.length - 1;
	let last = -1;
	while (lo <= hi) {
		const mid = (lo + hi) >> 1;
		const ts = items[mid].timestamp_start;
		if (ts == null) {
			hi = mid - 1;
			continue;
		}
		if (ts <= t) {
			last = mid;
			lo = mid + 1;
		} else {
			hi = mid - 1;
		}
	}
	if (last === -1) return [];
	const out: number[] = [];
	// Walk back to cover overlapping ranges; stop once both bounds are behind `t`.
	for (let i = last; i >= 0; i--) {
		const it = items[i];
		if (it.timestamp_start == null) continue;
		if (it.timestamp_end != null && t >= it.timestamp_start && t <= it.timestamp_end) {
			out.unshift(it.dialog_id);
		} else if (it.timestamp_end != null && t > it.timestamp_end) {
			break;
		}
	}
	return out;
};

/**
 * Like findActiveDialogIds, but when `t` falls in a silence gap between dialogs,
 * falls back to the most recent past dialog so the transcript keeps a stable anchor
 * for scroll + highlight on seek/playback. Before any dialog has played, returns [].
 */
export const findActiveOrNearestDialogIds = (items: IAudioVideoTranscriptItem[] | undefined | null, t: number): number[] => {
	if (!items || items.length === 0) return [];
	let lo = 0;
	let hi = items.length - 1;
	let last = -1;
	while (lo <= hi) {
		const mid = (lo + hi) >> 1;
		const ts = items[mid].timestamp_start;
		if (ts == null) {
			hi = mid - 1;
			continue;
		}
		if (ts <= t) {
			last = mid;
			lo = mid + 1;
		} else {
			hi = mid - 1;
		}
	}
	if (last === -1) return [];
	const out: number[] = [];
	for (let i = last; i >= 0; i--) {
		const it = items[i];
		if (it.timestamp_start == null) continue;
		if (it.timestamp_end != null && t >= it.timestamp_start && t <= it.timestamp_end) {
			out.unshift(it.dialog_id);
		} else if (it.timestamp_end != null && t > it.timestamp_end) {
			break;
		}
	}
	if (out.length > 0) return out;
	return [items[last].dialog_id];
};

/** Shallow equality for ordered id arrays — used to skip needless setState commits. */
export const sameIdList = (a: number[], b: number[]): boolean => {
	if (a.length !== b.length) return false;
	for (let i = 0; i < a.length; i++) {
		if (a[i] !== b[i]) return false;
	}
	return true;
};

/** Constants for HLS playback */
export const HLS_MAX_RETRIES = 2;
export const HLS_LOADING_TIMEOUT_MS = (() => {
	const eff = (navigator as Navigator & { connection?: { effectiveType?: string } }).connection?.effectiveType;
	if (eff === "4g") return 8000;
	if (eff === "3g") return 15000;
	if (eff === "2g" || eff === "slow-2g") return 25000;
	return 12000;
})();
